import React, {Fragment, useState} from 'react';
import {Modal, Tooltip, Button, Form, Upload, Typography, Input, notification, Checkbox} from 'antd';
import {ImportOutlined, UploadOutlined, DownloadOutlined} from '@ant-design/icons';
import {useHistory} from 'react-router-dom';
import Path from 'path';
import {camelCase} from 'change-case';

export function ImportSpecDialog({backend, params, disabled, spec}) {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState();
    const [createEmpty, setCreateEmpty] = useState(false);
    const [form] = Form.useForm();
    const history = useHistory();

    const initialValues = {
        name: '',
        message: 'initial import',
        empty: false,
        force: false,
        category: params.category,
    };

    async function handleImport() {
        try {
            setLoading(true);
            const values = await form.validateFields();
            if (values.empty) {
                values.specFile = null;
            } else {
                const uploadFile = values.specFile.file;
                values.specFile = uploadFile.originFileObj || uploadFile;
            }
            const result = (await backend.uploadSpecFile(values)) || {};
            // Backend may or may not echo {category, key}. We know what we
            // sent, so fall back to the user-supplied values; backend values
            // win if present (in case the server normalized the name).
            const category = result.category || values.category;
            const key = result.key || values.name;
            history.push(`/${params.env}/standards/${category}/${key}/latest/overview/`);
            setVisible(false);
            form.resetFields();
        } catch (err) {
            console.error(err);
            notification.error({title: err.message ? err.message : 'Validation Error', placement: 'bottomRight'});
        } finally {
            setLoading(false);
        }
    }

    function handleCancel() {
        setVisible(false);
    }

    function preprocessSpecFile({file}) {
        if (file) {
            let name = form.getFieldValue('name');
            if (!name) {
                name = Path.basename(file.name);
                name = name.substring(0, name.indexOf('.'));
                form.setFieldsValue({name: camelCase(name)});
            }
        }
    }

    async function callDownload() {
        await backend.downloadYml(params.category, params.key, params.version);
    }

    return (
        <Fragment>
            <Tooltip title="Import an OpenAPI specification file" placement="bottom">
                <Button
                    className="importButton"
                    id="import"
                    disabled={disabled}
                    type="secondary"
                    style={{marginLeft: 10, display: 'flex', justifyContent: 'center', alignItems: 'center'}}
                    icon={<ImportOutlined />}
                    onClick={() => setVisible(true)}
                />
            </Tooltip>
            <Tooltip title="Download" placement="bottom">
                <Button
                    className="importButton"
                    disabled={typeof spec !== 'undefined' && spec.length === 0 ? true : false}
                    type="secondary"
                    style={{marginLeft: 10, display: 'flex', justifyContent: 'center', alignItems: 'center'}}
                    icon={<DownloadOutlined />}
                    onClick={() => callDownload()}
                />
            </Tooltip>
            <Modal
                title="Import OpenAPI Specification"
                open={visible}
                destroyOnHidden
                footer={[
                    <Button className="commitCancel" key="back" onClick={handleCancel}>
                        Cancel
                    </Button>,
                    <Button className="commitButton" key="submit" type="primary" loading={loading} onClick={handleImport}>
                        Import
                    </Button>,
                ]}
                onCancel={handleCancel}>
                <Form form={form} initialValues={initialValues}>
                    <Typography.Text>Drag or select an OpenAPI V3 specification in Yaml (*.yml) format</Typography.Text>
                    <Form.Item name="name" label="Name" normalize={camelCase} rules={[{required: true}]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="category" label="Workgroup Name" rules={[{required: true}]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="message" label="Commit Message">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item name="empty" valuePropName="checked">
                        <Checkbox onChange={(e) => setCreateEmpty(e.target.checked)}>Start with an empty file</Checkbox>
                    </Form.Item>
                    <Form.Item name="force" valuePropName="checked">
                        <Checkbox>Force replacement of existing api (CAREFUL!)</Checkbox>
                    </Form.Item>
                    {!createEmpty && (
                        <Form.Item name="specFile" valuePropName="file" noStyle rules={[{required: true}]}>
                            <Upload.Dragger beforeUpload={() => false} onChange={preprocessSpecFile}>
                                <p className="ant-upload-drag-icon">
                                    <UploadOutlined />
                                </p>
                                <p className="ant-upload-text">Click or drag an OpenAPI V3 file (*.yml) to this area to upload</p>
                            </Upload.Dragger>
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </Fragment>
    );
}
